import clsx from 'clsx';
import { useFormik } from 'formik';
import { useState, useCallback, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { toAbsoluteUrl } from '../../../_library/helpers';
import { PageLink, PageTitle } from '../../../_library/layout/core';
import { RootState } from '../../../setup';
import { createUser, setUserAvatar, getCompanies } from '../../modules/CRUD/CRUD';
import { Company } from '../../../features/company/models/company.model';
import './styles/create_user.scss';
import { intersection, size } from 'lodash';

const accountBreadCrumbs: Array<PageLink> = [
	{
		title: 'Users',
		path: '',
		isSeparator: false,
		isActive: false,
	},
	{
		title: '',
		path: '',
		isSeparator: true,
		isActive: false,
	},
];

type SelectedLocation = { id: string; name: string; alias: string };

const requiresLocation = (role: string) => !!role && (size(intersection(role.split(','), ['SecchiAdmin', 'SecchiEngineerSenior'])) > 0);
const normalizeAlias = (alias: string) => alias.trim().toLowerCase();
const isAliasValid = (alias: string) => /^[A-Za-z0-9._-]+$/.test(alias.trim());
const clearAlias = (list: SelectedLocation[], id: string) => list.map(loc => (loc.id === id ? { ...loc, alias: '' } : loc));

function getAliasEmailParts(email: string) {
	const atIndex = email.indexOf('@');
	if (atIndex < 1) return { prefix: '', suffix: '' };
	const plusIndex = email.indexOf('+');
	const prefixEnd = plusIndex > -1 && plusIndex < atIndex ? plusIndex : atIndex;
	return { prefix: `${email.slice(0, prefixEnd)}+`, suffix: email.slice(atIndex) };
}

function CreateUser() {
	const navigate = useNavigate();
	const token: string = useSelector<RootState>(({ auth }) => auth.accessToken, shallowEqual) as string;
	const [loading, setLoading] = useState(false);
	const [avatarFile, setAvatarFile] = useState<any>(null);
	const [avatar, setAvatar] = useState('');
	const [isShowPwd, setIsShowPwd] = useState(false);
	const [locations, setLocations] = useState<Array<{ id: string; name: string }>>([]);
	const [selectedLocations, setSelectedLocations] = useState<SelectedLocation[]>([]);
	const [masterLocationId, setMasterLocationId] = useState<string>('');
	const [locationSelectValue, setLocationSelectValue] = useState<string>('');

	const initialValues = {
		firstName: '',
		lastName: '',
		email: '',
		password: '',
		changepassword: '',
		role: 'SecchiAdmin',
		locationIds: [] as string[],
		skipEmailVerification: false
	};

	const registrationSchema = Yup.object().shape({
		firstName: Yup.string()
									.min(3, 'Minimum 3 symbols')
									.max(50, 'Maximum 50 symbols')
									.required('First name is required'),
		email: Yup.string()
							.email('Wrong email format')
							.min(3, 'Minimum 3 symbols')
							.max(50, 'Maximum 50 symbols')
							.required('Email is required')
							.matches(/^(?!.*(@gmail.com|@hotmail.com|@outlook.com|@yahoo.com)).*$/, 'Not Business Email'),
		lastName: Yup.string()
								 .min(3, 'Minimum 3 symbols')
								 .max(50, 'Maximum 50 symbols')
								 .required('Last name is required'),
		password: Yup.string()
								 .min(12, 'Minimum 12 symbols')
								 .max(50, 'Maximum 50 symbols')
								 .required('Password is required')
								 .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{12,})/, 'Must include capital letter, special character and number'),
		changepassword: Yup.string()
											 .required('Password confirmation is required')
											 .when('password', {
												 is: (val: string) => (val && val.length > 0 ? true : false),
												 then: schema => schema.oneOf([Yup.ref('password')], 'Password and Confirm Password didn\'t match'),
											 }),
		role: Yup.string()
						 .required('Role is required'),
		locationIds: Yup.array()
						.of(Yup.string().required())
						.when('role', {
							is: (role: string) => requiresLocation(role),
							then: schema => schema.min(1, 'Select at least one location'),
						}),
		skipEmailVerification: Yup.boolean()
	});

	const applyLocations = (next: SelectedLocation[]) => {
		setSelectedLocations(next);
		formik.setFieldValue('locationIds', next.map(loc => loc.id));
	};

	const handleAddLocation = (evt: React.ChangeEvent<HTMLSelectElement>) => {
		const id = evt.target.value;
		setLocationSelectValue('');
		const location = locations.find(loc => loc.id === id);
		if (!location || selectedLocations.some(loc => loc.id === id)) return;

		applyLocations([...selectedLocations, { ...location, alias: '' }]);
		setMasterLocationId(prev => prev || id);
	};

	const handleRemoveLocation = (id: string) => {
		const remaining = selectedLocations.filter(loc => loc.id !== id);
		const nextMasterId = masterLocationId === id ? remaining[0]?.id || '' : masterLocationId;
		applyLocations(clearAlias(remaining, nextMasterId));
		setMasterLocationId(nextMasterId);
	};

	const handleSelectMaster = (id: string) => {
		setMasterLocationId(id);
		setSelectedLocations(prev => clearAlias(prev, id));
	};

	const handleAliasChange = (id: string, alias: string) => {
		setSelectedLocations(prev => prev.map(loc => (loc.id === id ? { ...loc, alias } : loc)));
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			let reader = new FileReader();
			let file = e.target.files[0];
			setAvatarFile(file);
			reader.onloadend = () => {
				if (reader && reader.result) {
					setAvatar(reader.result as string);
				}
			};
			reader.readAsDataURL(file);
		}
	};

	const removeAvatar = () => {
		setAvatar('');
	};

	const formik = useFormik({
		initialValues,
		validationSchema: registrationSchema,
		onSubmit: (values,
							 {
								 setStatus,
								 setSubmitting,
							 }) => {
			const roles = values.role.split(',');
			const submitLocations = requiresLocation(values.role) ? selectedLocations : [];
			const submitMasterId = submitLocations.length ? masterLocationId : '';
			const replicas = submitLocations.filter(loc => loc.id !== submitMasterId);

			if (replicas.some(loc => !loc.alias.trim())) {
				toast.error('Provide an alias for every non-master location');
				setSubmitting(false);
				return;
			}

			const { prefix: emailPrefix, suffix: emailSuffix } = getAliasEmailParts(values.email);
			const replicationTargets = replicas.map(loc => ({
				email: `${emailPrefix}${loc.alias.trim()}${emailSuffix}`,
				tenant_id: loc.id,
				roles,
			}));

			const finish = () => {
				setLoading(false);
				navigate('/users');
			};

			setLoading(true);
			setTimeout(() => {
				createUser(
					values.email,
					values.firstName,
					values.lastName,
					values.password,
					values.changepassword,
					roles,
					token,
					submitMasterId || undefined,
					values.skipEmailVerification,
					replicationTargets.length ? replicationTargets : undefined,
				)
					.then(({ data }) => {
						if (!avatarFile) return finish();

						const requestBody = new FormData();
						requestBody.append('file', avatarFile);
						return setUserAvatar(data.id, requestBody, token)
							.catch(error => console.log(error))
							.then(finish);
					})
					.catch((err) => {
						console.log(err);
						toast.error(err?.response?.data?.message || 'Error creating user');
						setLoading(false);
						setSubmitting(false);
					});
			}, 1000);
		},
	});

	const { prefix: aliasPrefix, suffix: aliasSuffix } = getAliasEmailParts(formik.values.email);
	const replicaLocations = selectedLocations.filter(loc => loc.id !== masterLocationId);
	const isDuplicateAlias = (alias: string) =>
		!!alias.trim() && replicaLocations.filter(loc => normalizeAlias(loc.alias) === normalizeAlias(alias)).length > 1;
	const areReplicasValid = !requiresLocation(formik.values.role) || (
		selectedLocations.length > 0 &&
		!!masterLocationId &&
		(replicaLocations.length === 0 || (!!aliasPrefix && !!aliasSuffix)) &&
		replicaLocations.every(loc => isAliasValid(loc.alias) && !isDuplicateAlias(loc.alias))
	);

	const loadLocations = useCallback(() => {
		getCompanies(token)
			.then(({data}) => {
				const sortedLocations = (data.items || [])
					.map((item: Company) => ({
						id: item.id,
						name: item.name,
					}))
					.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

				setLocations(sortedLocations);
			})
			.catch((err) => {
				console.error(err);
				toast.error('Error loading locations');
			});
	}, [token]);

	useEffect(() => {
		if (requiresLocation(formik.values.role)) {
			loadLocations();
		}
	}, [formik.values.role, loadLocations]);

	return (
		<>
			<PageTitle breadcrumbs={accountBreadCrumbs}>Create a User</PageTitle>
			<div className="card create_user_card">
				<div className="card-header border-0 pt-5"></div>
				<div className="card-body py-3">
					<div className="col-lg-12 text-center">
						<div className="d-flex align-items-center justify-content-center rounded bg-white bg-body shadow w-150px h-150px m-auto">
							<div
								className={`image-input image-input-outline w-120px h-120px ${avatar === '' ? 'image-input-empty' : ''}`}
								data-kt-image-input="true"
								style={{ backgroundImage: `url(${toAbsoluteUrl('/media/avatars/blank.png')})` }}
							>
								<div
									className="image-input-wrapper no-shadow shadow-none"
									style={{ backgroundImage: `url(${toAbsoluteUrl(avatar)})` }}
								></div>
								<label
									className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
									data-kt-image-input-action="change"
									data-bs-toggle="tooltip"
									title=""
									data-bs-original-title="Change avatar">
									<i className="bi bi-pencil-fill fs-7"></i>
									<input
										type="file"
										name="avatar"
										accept=".png, .jpg, .jpeg"
										onChange={handleFileChange} />
									<input
										type="hidden"
										name="avatar_remove" />
								</label>
								<span
									className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
									data-kt-image-input-action="remove"
									data-bs-toggle="tooltip"
									title=""
									data-bs-original-title="Remove avatar"
									onClick={removeAvatar}
								>
									<i className="bi bi-x fs-2"></i>
								</span>
							</div>
						</div>
						<div className="form-text mt-5">Set the product thumbnail image. Only
																						*.png, *.jpg and *.jpeg image files
																						are accepted
						</div>
					</div>
					<div className="w-lg-700px p-lg-15 p-10 bg-white rounded mx-auto">
						<form
							className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
							noValidate
							id="kt_login_signup_form"
							onSubmit={formik.handleSubmit}
						>
							<div className="row fv-row mb-7">
								<div className="col-xl-6">
									<div className="fv-row">
										<label className="form-label fw-bolder text-dark fs-6">First Name</label>
										<input
											placeholder="First Name"
											type="text"
											autoComplete="off"
											{...formik.getFieldProps('firstName')}
											className={clsx(
												'form-control form-control-lg form-control-solid',
												{
													'is-invalid': formik.touched.firstName && formik.errors.firstName,
												},
												{
													'is-valid': formik.touched.firstName && !formik.errors.firstName,
												},
											)}
										/>
										{formik.touched.firstName && formik.errors.firstName && (
											<div className="fv-plugins-message-container">
												<div className="fv-help-block">
													<span role="alert">{formik.errors.firstName}</span>
												</div>
											</div>
										)}
									</div>
								</div>
								<div className="col-xl-6">
									<div className="fv-row">
										<label className="form-label fw-bolder text-dark fs-6">Last Name</label>
										<input
											placeholder="Last Name"
											type="text"
											autoComplete="off"
											{...formik.getFieldProps('lastName')}
											className={clsx(
												'form-control form-control-lg form-control-solid',
												{
													'is-invalid': formik.touched.lastName && formik.errors.lastName,
												},
												{
													'is-valid': formik.touched.lastName && !formik.errors.lastName,
												},
											)}
										/>
										{formik.touched.lastName && formik.errors.lastName && (
											<div className="fv-plugins-message-container">
												<div className="fv-help-block">
													<span role="alert">{formik.errors.lastName}</span>
												</div>
											</div>
										)}
									</div>
								</div>
							</div>

							<div className="fv-row mb-7">
								<label className="form-label fw-bolder text-dark fs-6">Email</label>
								<input
									placeholder="Email"
									type="email"
									autoComplete="off"
									{...formik.getFieldProps('email')}
									className={clsx(
										'form-control form-control-lg form-control-solid',
										{ 'is-invalid': formik.touched.email && formik.errors.email },
										{
											'is-valid': formik.touched.email && !formik.errors.email,
										},
									)}
								/>
								{formik.touched.email && formik.errors.email && (
									<div className="fv-plugins-message-container">
										<div className="fv-help-block">
											<span role="alert">{formik.errors.email}</span>
										</div>
									</div>
								)}
							</div>

							<div
								className="mb-10 fv-row"
								data-kt-password-meter="true">
								<div className="mb-1">
									<label className="form-label fw-bolder text-dark fs-6">Password</label>
									<div className="position-relative mb-3">
										<input
											type={isShowPwd ? 'text' : 'password'}
											placeholder="Password"
											autoComplete="off"
											{...formik.getFieldProps('password')}
											className={clsx(
												'form-control form-control-lg form-control-solid',
												{
													'is-invalid': formik.touched.password && formik.errors.password,
												},
												{
													'is-valid': formik.touched.password && !formik.errors.password,
												},
											)}
										/>
										{formik.touched.password && formik.errors.password && (
											<div className="fv-plugins-message-container">
												<div className="fv-help-block">
													<span role="alert">{formik.errors.password}</span>
												</div>
											</div>
										)}
										<button
											type="button"
											className="position-absolute btn p-0 btn_show_password"
											onClick={() => setIsShowPwd(!isShowPwd)}
										>
											{
												isShowPwd ?
													<i className="bi bi-eye-slash-fill fs-3"></i> :
													<i className="bi bi-eye-fill fs-3"></i>
											}
										</button>
									</div>
								</div>
							</div>

							<div className="fv-row mb-5">
								<label className="form-label fw-bolder text-dark fs-6">Confirm Password</label>
								<input
									type="password"
									placeholder="Password confirmation"
									autoComplete="off"
									{...formik.getFieldProps('changepassword')}
									className={clsx(
										'form-control form-control-lg form-control-solid',
										{
											'is-invalid': formik.touched.changepassword && formik.errors.changepassword,
										},
										{
											'is-valid': formik.touched.changepassword && !formik.errors.changepassword,
										},
									)}
								/>
								{formik.touched.changepassword && formik.errors.changepassword && (
									<div className="fv-plugins-message-container">
										<div className="fv-help-block">
											<span role="alert">{formik.errors.changepassword}</span>
										</div>
									</div>
								)}
							</div>

							<div className="fv-row mb-7">
								<label className="form-label fw-bolder text-dark fs-6">Role</label>
								<select
									{...formik.getFieldProps('role')}
									className={clsx(
										'form-control form-control-lg form-control-solid',
										// {
										//     'is-invalid': formik.touched.role && formik.errors.role,
										// },
										// {
										//     'is-valid': formik.touched.role && !formik.errors.role,
										// }
									)}
									style={{ appearance: 'auto' }}
								>
									<option value=""></option>
									<option value="SuperAdmin,Provisioner">Super Admin & Provisioner</option>
									<option value="SuperAdmin">Super Admin</option>
									<option value="Supervisor">Supervisor</option>
									<option value="Provisioner">Provisioner</option>
									<option value="SecchiAdmin">Secchi Admin</option>
									<option value="SecchiAdmin,SecchiEngineerSenior">Secchi Admin & Secchi Senior Engineer</option>
								</select>
								{formik.touched.role && formik.errors.role && (
									<div className="fv-plugins-message-container">
										<div className="fv-help-block">
											<span role="alert">{formik.errors.role}</span>
										</div>
									</div>
								)}
							</div>

							{requiresLocation(formik.values.role) && (
								<div className="fv-row mb-7">
									<label className="form-label fw-bolder text-dark fs-6 d-flex align-items-center gap-2">
										Select Location
										{selectedLocations.length > 0 && (
											<span className="badge badge-light-primary">{selectedLocations.length} selected</span>
										)}
									</label>
									<select
										onChange={handleAddLocation}
										value={locationSelectValue}
										required
										aria-required="true"
										aria-invalid={typeof formik.errors.locationIds === 'string'}
										className={clsx(
											'form-control form-control-lg form-control-solid'
										)}
										style={{ appearance: 'auto' }}
									>
										<option value="" disabled>
											{selectedLocations.length > 0 ? 'Add another location...' : 'Select a location'}
										</option>
										{locations
											.filter(location => !selectedLocations.some(loc => loc.id === location.id))
											.map((location) => (
												<option key={location.id} value={location.id}>
													{location.name}
												</option>
											))}
									</select>
									{typeof formik.errors.locationIds === 'string' && (
										<div className="fv-plugins-message-container">
											<div className="fv-help-block">
												<span role="alert">{formik.errors.locationIds}</span>
											</div>
										</div>
									)}
									<div className="form-text">Pick a location to add it below. Repeat to add more, then choose a master.</div>

									{selectedLocations.length > 0 && (
										<div className="table-responsive mt-4">
											<table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
												<thead>
													<tr className="fw-bolder fw-bold">
														<th>Location</th>
														<th>Alias</th>
														<th className="text-end">Actions</th>
													</tr>
												</thead>
												<tbody>
													{selectedLocations.map((location) => (
														<tr key={location.id}>
															<td>
																<div className="d-flex align-items-center">
																	<div className="form-check form-check-custom form-check-solid me-3">
																		<input
																			type="radio"
																			name="masterLocation"
																			className="form-check-input"
																			checked={masterLocationId === location.id}
																			onChange={() => handleSelectMaster(location.id)}
																			title="Set as master profile"
																		/>
																	</div>
																	<div className="d-flex flex-column">
																		<span className="fw-bolder">{location.name}</span>
																		{masterLocationId === location.id && <span className="fw-light">Master</span>}
																	</div>
																</div>
															</td>
															<td>
																{masterLocationId !== location.id ? (
																	<div className="d-flex align-items-center">
																		<span className="align-content-center">{aliasPrefix}</span>
																		<input
																			type="text"
																			placeholder="alias"
																			autoComplete="off"
																			value={location.alias}
																			onChange={evt => handleAliasChange(location.id, evt.target.value)}
																			className={clsx('form-control form-control-sm w-150px', {
																				'is-invalid': !!location.alias && (!isAliasValid(location.alias) || isDuplicateAlias(location.alias)),
																			})}
																		/>
																		<span className="align-content-center">{aliasSuffix}</span>
																	</div>
																) : (
																	<span className="text-muted">&mdash;</span>
																)}
															</td>
															<td>
																<div className="d-flex justify-content-end align-items-center gap-3 flex-nowrap">
																	<button
																		type="button"
																		className="btn btn-icon btn-sm p-0"
																		onClick={() => handleRemoveLocation(location.id)}
																		aria-label="Remove location"
																	>
																		<i className="bi bi-trash fs-3 text-danger"></i>
																	</button>
																</div>
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									)}
								</div>
							)}

							<div className="fv-row mb-7">
								<div className="form-check form-check-custom form-check-solid">
									<input
										className="form-check-input"
										type="checkbox"
										{...formik.getFieldProps('skipEmailVerification')}
										checked={formik.values.skipEmailVerification}
									/>
									<label className="form-check-label fw-bolder text-dark fs-6">
										Skip Email Verification
									</label>
								</div>
							</div>

							<div className="text-center mt-10">
								<button
									type="submit"
									id="kt_sign_up_submit"
									className="btn btn-lg btn-primary min-w-150px mb-5"
									disabled={formik.isSubmitting || !formik.isValid || !areReplicasValid}
								>
									{!loading && <span className="indicator-label">Create</span>}
									{loading && (
										<span
											className="indicator-progress"
											style={{ display: 'block' }}
										>
											Please wait...{' '}
											<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
										</span>
									)}
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</>
	);
}

export { CreateUser };
